import classnames from "classnames";
import ContentIdsComponent from "modules/static-pages/content-ids/contentIds";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import contentPerosnasJson from "resources/json/contentPersonas.json";
import useScreenSize from "../../hoc/useScreenSize";
import { DEFAULT_CATEGORY_BANNER } from "../../modules/site-structure/common/make-model-year-utils";
import { renderHTML } from "../../util/DomHelper";
import HideIf from "../../util/HideIf";
import { preformContentLinkClick } from "../../util/url/preformContentLinkClick";
import BreadcrumbCompact from "../breadcrumb/BreadcrumbCompact";
import BreadcrumbRegular from "../breadcrumb/BreadcrumbRegular";
import VizSensor from "../design-system/VizSensor";
import SharedButtons from "../design-system/sharedButtons/SharedButton";
import ArticleGuide from "../guide-articles/ArticleGuide";
import { checkIfResponsiveMediaIsPresent } from "./ArticleLandingPage.utils";
import Articles from "./Articles";
import NewsLetterForm from "./NewsLetterForm";
import TableOfContent from "./TableOfContent";
import AuthorInfo from "./authorInfo/AuthorInfo";
import styles from "./landingPage.module.scss";

const FeaturedProducts = dynamic(() => import(/* webpackChunkName: "featured-products" */ "component/featured-products/featured-products.component"));
const CategoryBannerRegular = dynamic(() => import(/* webpackChunkName: "category-banner-regular" */ "component/category/banner/CategoryBannerRegular"));
const VideoSection = dynamic(() => import(/* webpackChunkName: "video-section" */ "component/landing-page/VideoSection"));

function ArticleLandingPage(props: any) {
  const {
    data, pageType, contentClassNames, greyBackgroundInArticle = false, hideBreadcrumbs = false,
  } = props;
  const {
    featuredSection, makeModelTiers, pageDetails, breadcrumbs = [], links = {}, authorInformation,
    showNewsLetter = true, showBreadcrumbs = true,
  } = data;
  const { contentIds = [] } = links;
  const featuredLinks = featuredSection?.featuredLinks || data.featuredLinks;
  const featuredSectionTitle = featuredSection?.featuredSectionTitle || "Featured Products";
  const useVizSensor = false;
  const h1 = data.headers?.h1 || data.pageDetails?.pageDisplayableTitle;
  const [isMobile,, isDesktop] = useScreenSize();
  const router = useRouter();
  const { t } = useTranslation("common");
  const contentIdsWithTitle = contentIds.map((eachContent) => {
    if (eachContent.uiDetails?.uiType === "FAQ") {
      return {
        ...eachContent,
        uiDetails: {
          ...eachContent.uiDetails,
          content: {
            title: "FAQ",
            ...eachContent.uiDetails.content,
          },
        },
      };
    }
    return eachContent;
  });

  // const filterFaqContent = contentIds && contentIds
  //   .find((eachContent: any) => eachContent?.uiDetails?.uiType === "FAQ")?.uiDetails.content.faqs;

  let { lastUpdateTimestamp = "" } = data;
  // en-GB date format is added to fix hydration error.
  // On server side date is 5/06/2022 & on client side date is 05/06/2022
  if (lastUpdateTimestamp) lastUpdateTimestamp = new Date(lastUpdateTimestamp).toLocaleDateString("en-GB");

  const numberOfSections = (data.articles && data.articles.map(
    (eachArticle: { sections: object[] }) => eachArticle.sections.length,
  ).reduce((sum: number, current: number) => sum + current, 0)) || 1;

  const isResponsiveBannerMediaPresent = checkIfResponsiveMediaIsPresent(data?.responsiveBannerMedia);

  const authorName = contentPerosnasJson[authorInformation?.persona]?.name || "Partsavatar";

  function handleContentClick(e: any) {
    if (e.defaultPrevented) return;

    const target = e.target as HTMLElement;
    if (!target || !target.closest("a")) return;

    preformContentLinkClick(router, e);
  }

  return (
    <>
      <Head>
        <meta name="format-detection" content="telephone=no" />
      </Head>
      <div className="p-l-10 p-r-10 p-t-10">
        {
          isMobile && !hideBreadcrumbs && (
            <BreadcrumbCompact
              {...{
                breadCrumbsV2: [breadcrumbs],
              }}
              className="m-t-10"
            />
          )
        }

        <h1 className={classnames({ [styles.text_center]: pageType === "STATIC_PAGE" }, "m-t-0")}>
          <strong>{h1}</strong>
        </h1>
        <HideIf show={!isResponsiveBannerMediaPresent}>
          <HideIf show={data.bannerImages && data.bannerImages.desktopImage}>
            <CategoryBannerRegular
              {...{
                bannerData: {
                  bannerImageUrl: isMobile ? data.bannerImages?.mobileImage : data.bannerImages?.desktopImage,
                  fallbackImage: DEFAULT_CATEGORY_BANNER,
                },
                staticContent: {
                  pageDetails: {
                    pageDisplayableTitle: h1,
                  },
                },
              }}
              breadcrumb={isDesktop && showBreadcrumbs ? (
                <BreadcrumbRegular {...{
                  pageDetails,
                  breadCrumbsV2: [breadcrumbs],
                }}
                />
              ) : null}
            />
          </HideIf>
        </HideIf>

        <HideIf show={isResponsiveBannerMediaPresent}>
          <CategoryBannerRegular
            responsiveBannerMedia={data.responsiveBannerMedia}
            breadcrumb={isDesktop && showBreadcrumbs ? (
              <BreadcrumbRegular {...{
                pageDetails,
                breadCrumbsV2: [breadcrumbs],
              }}
              />
            ) : null}
          />
        </HideIf>

        <HideIf show={pageType !== "STATIC_PAGE" && authorInformation?.persona !== "NA"}>
          <div className={`d-flex ${styles.article_meta}`}>
            <div className={styles.article_author}>
              {`${t("by")} `}
              <strong>

                {authorName}
                {" "}
              </strong>
              {lastUpdateTimestamp}
              <br />
              {`${numberOfSections * 2 > 24 ? 24 : numberOfSections * 2} ${t("minutes read")}`}
            </div>
            <SharedButtons title={h1} />
          </div>
        </HideIf>

        <div className={contentClassNames}>

          {data.d
        && data.d.length > 0
        && data.d.map((desc: any, index: number) => (
          <div
            key={index}
            className={`m-t-10 m-b-20 ${styles.description}`}
            onClick={handleContentClick}
            onKeyPress={handleContentClick}
            dangerouslySetInnerHTML={renderHTML(desc)}
          />
        ))}

          <HideIf show={data.showTableOfContent}>

            <TableOfContent items={data.articles} />
          </HideIf>
          {data.articles && data.articles.length > 0 && (
            <div>

              <Articles
                className="section p-0 m-t-20"
                items={[data.articles[0]]}
                imgSrc={`${process.env.NEXT_PUBLIC_CMS}/`}
                useVizSensor={useVizSensor}
                greyBackgroundInArticle={greyBackgroundInArticle}
              />
              {featuredLinks && featuredLinks.length !== 0 && (
                <div className={styles.featuredSection}>
                  <span className={styles.featured_section_heading}>
                    <strong>{featuredSectionTitle}</strong>
                  </span>
                  <FeaturedProducts
                    products={featuredLinks}
                  />
                </div>
              )}
              {data.articles.length > 1 && (
                <Articles
                  className="section p-0 m-t-20"
                  items={data.articles.slice(1)}
                  imgSrc={`${process.env.NEXT_PUBLIC_CMS}/`}
                  useVizSensor={useVizSensor}
                  greyBackgroundInArticle={greyBackgroundInArticle}
                  // obd page short term fix for interlinking
                  // https://partsavatar.ca/p2803-obd-ii-trouble-code-transmission-range-sensor-b-circuit-high-solution-powertrain-control-module
                  {...{ makeModelTiers, pageDetails }}
                />
              )}
              {/* https://integ.partsavatar.ca/brake-rotors-buying-guide#articles */}

              <ArticleGuide {...{ articles: links?.articles }} className={styles.guides_article} tileWidth={240} />
              {data.videos && (
                <VizSensor forceSSR>

                  <VideoSection videos={data.videos} />
                </VizSensor>
              )}

              {/* https://partsavatar.ca/abs-system-working-and-symptoms */}
              {/* {filterFaqContent && (
              <div>
                <h2>FAQ</h2>
                <FAQ filterFaq={filterFaqContent} />
              </div>
            )} */}

            </div>
          )}
          <ContentIdsComponent contentIds={contentIdsWithTitle} />
          <HideIf show={pageType !== "STATIC_PAGE"}>
            { showNewsLetter && <NewsLetterForm /> }
            <AuthorInfo
              className="m-t-20"
              authorInformation={authorInformation}
              contentPerosnasJson={contentPerosnasJson}
            />
          </HideIf>
        </div>
      </div>
    </>
  );
}

export default ArticleLandingPage;
